import get from 'lodash/fp/get';
import moment from 'moment';
import { converge, add } from 'ramda';

import {
  ADD_VALUE_TO_SEGMENT,
  RESET_SEGMENT,
  SET_IS_FETCHING,
  UNSET_IS_FETCHING,
} from './actionTypes';
import {
  NET_SERVICE_REVENUE_SEGMENT,
  NEW_CUSTOMERS_SEGMENT,
  NET_PRODUCT_REVENUE_SEGMENT,
  PRODUCT_COUNT_SEGMENT,
  SERVICE_COUNT_SEGMENT,
  TOTAL_REVENUE_SEGMENT,
  TOTAL_REVENUE_WITH_TAX_SEGMENT,
} from './constants';
import config from '../../../../config';
import {
  fetchMetrics,
  resetDirty,
} from '../metrics/actions';
import Criteria from '../metrics/Criteria';

const resetSegment = segment => ({
  type: RESET_SEGMENT(segment),
});

const addValueToSegment = segment => (key, value) => ({
  type: ADD_VALUE_TO_SEGMENT(segment),
  payload: { key, value },
});

const resetTotalRevenue = resetSegment(TOTAL_REVENUE_SEGMENT);
const addValueToTotalRevenue = addValueToSegment(TOTAL_REVENUE_SEGMENT);
const resetTotalRevenueWithTax = resetSegment(TOTAL_REVENUE_WITH_TAX_SEGMENT);
const addValueToTotalRevenueWithTax = addValueToSegment(TOTAL_REVENUE_WITH_TAX_SEGMENT);
const resetNetServiceRevenue = resetSegment(NET_SERVICE_REVENUE_SEGMENT);
const addValueToNetServiceRevenue = addValueToSegment(NET_SERVICE_REVENUE_SEGMENT);
const resetServiceCount = resetSegment(SERVICE_COUNT_SEGMENT);
const addValueToServiceCount = addValueToSegment(SERVICE_COUNT_SEGMENT);
const resetNetProductRevenue = resetSegment(NET_PRODUCT_REVENUE_SEGMENT);
const addValueToNetProductRevenue = addValueToSegment(NET_PRODUCT_REVENUE_SEGMENT);
const resetProductCount = resetSegment(PRODUCT_COUNT_SEGMENT);
const addValueToProductCount = addValueToSegment(PRODUCT_COUNT_SEGMENT);
const resetNewCustomers = resetSegment(NEW_CUSTOMERS_SEGMENT);
const addValueToNewCustomers = addValueToSegment(NEW_CUSTOMERS_SEGMENT);

const getGrossRevenue = get('grossTotal');
const getGrossRevenueWithTax = converge(
  add,
  [get('grossTotal'), get('tax')],
);
const getNetTotal = get('netTotal');
const getQuantity = get('quantity');
const getCount = get('count');

const resetAnalytics = (dispatch) => {
  dispatch(resetTotalRevenue);
  dispatch(resetTotalRevenueWithTax);
  dispatch(resetNetServiceRevenue);
  dispatch(resetServiceCount);
  dispatch(resetProductCount);
  dispatch(resetNetProductRevenue);
  dispatch(resetNewCustomers);
};

const fetchDataFromMetricsEndpoint = (shopId, barberId, key, from, to, brandId, shopFilter) => (dispatch) => {
  const criteria = new Criteria({
    fromDate: from,
    toDate: to,
    shopId,
    ...(barberId && { barberFilter: [barberId] }),
    barberId,
    brandId,
    shopFilter,
  });
  return fetchMetrics(criteria, [
    'sales',
    'serviceSales',
    'productSales',
    'newCustomerCount',
  ]).then((data) => {
    dispatch(addValueToTotalRevenue(key, getGrossRevenue(data.sales)));
    dispatch(addValueToTotalRevenueWithTax(key, getGrossRevenueWithTax(data.sales)));
    dispatch(addValueToNetServiceRevenue(key, getNetTotal(data.serviceSales)));
    dispatch(addValueToServiceCount(key, getQuantity(data.serviceSales)));
    dispatch(addValueToNetProductRevenue(key, getNetTotal(data.productSales)));
    dispatch(addValueToProductCount(key, getQuantity(data.productSales)));
    dispatch(addValueToNewCustomers(key, getCount(data.newCustomerCount)));
  });
};

const setIsFetching = key => ({ type: SET_IS_FETCHING, payload: key });
const unsetIsFetching = key => ({ type: UNSET_IS_FETCHING, payload: key });

// eslint-disable-next-line import/prefer-default-export
export const fetchAnalyticsData =
(shopId, barberId, key = 'first', from, to, brandId, shopFilter) => async (dispatch) => {
  if (!(shopId || brandId) || !from || !to) return null;

  const fromDateTime = moment(from).format(config.dateFormatISO);
  const toDateTime = moment(to).format(config.dateFormatISO);
  dispatch(setIsFetching(key));
  dispatch(resetAnalytics);
  dispatch(resetDirty());
  await dispatch(fetchDataFromMetricsEndpoint(shopId, barberId, key, fromDateTime, toDateTime, brandId, shopFilter));

  return dispatch(unsetIsFetching(key));
};
